import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@/lib/zod-resolver";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock3, XCircle } from "lucide-react";
import {
  getGetMeQueryKey,
  getListMyWorkspaceAccessRequestsQueryKey,
  useCreateWorkspaceAccessRequest,
  useListMyWorkspaceAccessRequests,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";

const requestSchema = z.object({
  ueiCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{12}$/, "UEI must be 12 alphanumeric characters"),
  note: z.string().max(500, "Keep your note under 500 characters").optional(),
});

type RequestValues = z.infer<typeof requestSchema>;

function errorMessage(error: unknown): string {
  return (error as { message?: string })?.message ?? "Could not submit the access request.";
}

function statusLabel(status: string): string {
  return status === "approved" ? "Approved" : status === "declined" ? "Declined" : "Pending review";
}

export function WorkspaceAccessRequestPanel({
  initialUeiCode = "",
  onSubmitted,
}: {
  initialUeiCode?: string;
  onSubmitted?: () => void;
}) {
  const qc = useQueryClient();
  const [submitted, setSubmitted] = useState<{
    workspaceName: string;
    status: "pending" | "approved" | "declined";
    reviewReason?: string | null;
  } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { ueiCode: initialUeiCode, note: "" },
  });

  useEffect(() => {
    if (initialUeiCode) form.setValue("ueiCode", initialUeiCode);
  }, [form, initialUeiCode]);

  const createMut = useCreateWorkspaceAccessRequest({
    mutation: {
      onSuccess: (result) => {
        setSubmitted(result);
        setSubmitError(null);
        qc.invalidateQueries({ queryKey: getListMyWorkspaceAccessRequestsQueryKey() });
        onSubmitted?.();
      },
      onError: (error) => setSubmitError(errorMessage(error)),
    },
  });

  const submit = (values: RequestValues) => {
    setSubmitError(null);
    createMut.mutate({
      data: {
        ueiCode: values.ueiCode.trim().toUpperCase(),
        note: values.note?.trim() || null,
      },
    });
  };

  return (
    <div className="space-y-3" data-testid="workspace-access-request">
      <div>
        <h3 className="font-medium">Request access to your company workspace</h3>
        <p className="text-sm text-muted-foreground">
          Enter the 12-character SAM.gov UEI. Workspace administrators can approve you as a
          Member. They will see your name, email, and optional note.
        </p>
      </div>
      <form onSubmit={form.handleSubmit(submit)} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="workspace-access-uei">SAM.gov UEI</Label>
          <Input
            id="workspace-access-uei"
            placeholder="12-character UEI"
            maxLength={12}
            {...form.register("ueiCode")}
            data-testid="input-access-request-uei"
          />
          {form.formState.errors.ueiCode && (
            <p className="text-xs text-destructive">{form.formState.errors.ueiCode.message}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="workspace-access-note">Note (optional)</Label>
          <Textarea
            id="workspace-access-note"
            rows={3}
            maxLength={500}
            placeholder="Tell the workspace admins who you are or why you need access."
            {...form.register("note")}
            data-testid="input-access-request-note"
          />
          <p className="text-right text-xs text-muted-foreground">
            {(form.watch("note") ?? "").length}/500
          </p>
        </div>
        {submitError && (
          <p role="alert" className="text-sm text-destructive" data-testid="access-request-error">
            {submitError}
          </p>
        )}
        <Button
          type="submit"
          disabled={createMut.isPending}
          data-testid="button-submit-access-request"
        >
          {createMut.isPending ? "Submitting…" : "Request access"}
        </Button>
      </form>
      {submitted && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm"
          data-testid="access-request-submitted"
        >
          <p className="font-medium">
            {submitted.status === "approved"
              ? `Access approved for ${submitted.workspaceName}.`
              : submitted.status === "declined"
                ? `Access was declined for ${submitted.workspaceName}.`
                : `Request sent to ${submitted.workspaceName}.`}
          </p>
          <p className="mt-1 text-muted-foreground">
            {submitted.status === "pending"
              ? "You can keep using your other workspaces while an administrator reviews it."
              : submitted.status === "approved"
                ? "Refresh or open the workspace switcher to use it."
                : submitted.reviewReason || "You can continue to use other workspaces you belong to."}
          </p>
        </div>
      )}
    </div>
  );
}

export function WorkspaceAccessRequestStatusList() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useListMyWorkspaceAccessRequests({
    query: {
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchInterval: 30_000,
      queryKey: getListMyWorkspaceAccessRequestsQueryKey(),
    },
  });

  useEffect(() => {
    if (data?.some((request) => request.status === "approved")) {
      qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
    }
  }, [data, qc]);

  if (isLoading) {
    return (
      <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4 text-primary" /> Checking access requests…
      </div>
    );
  }
  if (isError) {
    return (
      <div role="alert" className="space-y-2 text-sm text-muted-foreground">
        <p>Access request status is temporarily unavailable.</p>
        <Button type="button" size="sm" variant="outline" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (!data || data.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="access-request-status">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Your access requests
      </h3>
      {data.map((request) => (
        <div
          key={request.id}
          className="rounded-md border border-border bg-card p-3"
          data-testid={`row-my-access-request-${request.id}`}
        >
          <div className="flex items-start gap-2">
            {request.status === "approved" ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            ) : request.status === "declined" ? (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
            ) : (
              <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium">{request.workspaceName}</p>
              <p className="text-xs text-muted-foreground">UEI {request.ueiCode}</p>
            </div>
            <Badge variant={request.status === "approved" ? "default" : "outline"}>
              {statusLabel(request.status)}
            </Badge>
          </div>
          {request.status === "declined" && request.reviewReason && (
            <p className="mt-2 text-sm text-muted-foreground">{request.reviewReason}</p>
          )}
        </div>
      ))}
    </div>
  );
}